import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Schema } from "yup";
import FormikSelect from "../../formik-form-components/FormikSelect";
import {
  FieldGroup,
  GerberFileForm,
  GerberFileFormTitle,
  FieldGroupNote,
} from "../OrderBoardStyles";
import { useFormikContext } from "formik";
import { UploadFormValues } from "../UploadForm";
import _ from "lodash";
import {
  COPPER_BOTTOM,
  COPPER_INNER_LAYERS,
  COPPER_TOP,
  PROFILE,
  SILKSCREEN_BOTTOM,
  SILKSCREEN_TOP,
  SOLDERMASK_BOTTOM,
  SOLDERMASK_TOP,
  SOLDERPASTE_BOTTOM,
  SOLDERPASTE_TOP,
} from "./util/strategies/bestMatchFileSuggestions";
import Link from "next/link";
import { useAppSelector } from "../../../utils/hooks/store";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../utils/group";
import { useReadGerberZip } from "./util/readGerberZip";
import SelectBoard from "../../SelectBoard";
import { Board, GerberFileLayerMapping } from "../../../reducers/uploadedBoard";
import Checkbox from "../../common/Checkbox";
import FormikErrorMessage from "../../formik-form-components/FormikErrorMessage";
import {
  describeDuplicateLayerFiles,
  findDuplicateLayerFiles,
} from "./util/duplicateLayerFiles";

interface ExpectedGerberFile {
  name: string;
  required: boolean;
  multiple?: boolean;
  hint?: string;
}

const EXPECTED_GERBER_FILES: ExpectedGerberFile[] = [
  { name: PROFILE, required: true },
  { name: "drills", multiple: true, required: false },
  { name: COPPER_TOP, required: true },
  {
    name: COPPER_INNER_LAYERS,
    required: false,
    multiple: true,
    hint: "Order from top to bottom",
  },
  { name: COPPER_BOTTOM, required: true },
  { name: SOLDERMASK_TOP, required: true },
  { name: SOLDERMASK_BOTTOM, required: true },
  { name: SOLDERPASTE_TOP, required: false },
  { name: SOLDERPASTE_BOTTOM, required: false },
  { name: SILKSCREEN_TOP, required: false },
  { name: SILKSCREEN_BOTTOM, required: false },
];

export const EMPTY_GERBER_FILE_MAPPING = EXPECTED_GERBER_FILES.reduce(
  (resultingSchema, expectedGerberFile) => {
    if (expectedGerberFile.multiple) {
      resultingSchema[expectedGerberFile.name] = [];
    } else {
      resultingSchema[expectedGerberFile.name] = "";
    }
    return resultingSchema;
  },
  {} as GerberFileLayerMapping,
);

export const GERBER_FILE_LAYER_MAPPING_SCHEMA = Yup.object().shape(
  EXPECTED_GERBER_FILES.reduce(
    (resultingSchema, expectedGerberFile) => {
      if (expectedGerberFile.multiple) {
        resultingSchema[expectedGerberFile.name] = expectedGerberFile.required
          ? Yup.array()
              .of(Yup.string())
              .required(
                `Specifying the ${expectedGerberFile.name} layer(s) is required`,
              )
              .min(1)
          : Yup.array().of(Yup.string());
      } else {
        resultingSchema[expectedGerberFile.name] = expectedGerberFile.required
          ? Yup.string().required(
              `Specifying the ${expectedGerberFile.name} layer is required`,
            )
          : Yup.string().nullable();
      }
      return resultingSchema;
    },
    {} as { [id: string]: Schema },
  ),
);

export const BOM_FILE_INFO_SCHEMA = Yup.object().shape({
  file_name: Yup.string().required(),
  designator_column: Yup.string().required(
    "Specifying the designator column is required when uploading a BOM",
  ),
  mpn_column: Yup.string().required(
    "Specifying the part number column is required when uploading a BOM",
  ),
  value_text_column: Yup.string().nullable(),
  footprint_column: Yup.string().nullable(),
  manufacturer_column: Yup.string().nullable(),
});

export const PNP_FILE_INFO_SCHEMA = Yup.object().shape({
  file_name: Yup.string().required(),
  designator_column: Yup.string().required(
    "Specifying the designator column is required when uploading a pick and place file",
  ),
  x_column: Yup.string().required(
    "Specifying the X column is required when uploading a pick and place file",
  ),
  y_column: Yup.string().required(
    "Specifying the Y column is required when uploading a pick and place file",
  ),
  rotation_column: Yup.string().required(
    "Specifying the rotation column is required when uploading a pick and place file",
  ),
  mount_type_column: Yup.string().required(
    "Specifying the mount type column is required when uploading a pick and place file",
  ),
  unit: Yup.string().required(
    "Specifying the unit is required when uploading a pick and place file",
  ),
});

export const UNIT_OPTIONS = [
  {
    value: "mm",
    label: "mm",
  },
  {
    value: "inch",
    label: "inch",
  },
  {
    value: "milinch",
    label: "mil",
  },
];

export const fieldTitle = (expectedGerberFile: ExpectedGerberFile) => {
  const capitalizedName = _.capitalize(
    expectedGerberFile.name.replaceAll("_", " "),
  );
  const hint = expectedGerberFile.hint ? ` (${expectedGerberFile.hint})` : "";
  const suffix = expectedGerberFile.required ? " (Required)" : "";
  return `${capitalizedName}${hint}${suffix}`;
};

export interface ExtrepEntry {
  extension: string;
  description: string;
}

export enum FileSelectionStrategy {
  BEST_GUESS = "Best guess",
  PREVIOUS_UPLOAD = "Previous upload",
  INCLUDED_FILE_MAPPING = "Included file mapping",
}

export default function GerberFileIdentifier({
  file,
  gerbersOnly,
  mappingSuggestionsDisabled,
}: {
  file?: Blob;
  gerbersOnly?: boolean;
  mappingSuggestionsDisabled?: boolean;
}) {
  const { values } = useFormikContext<UploadFormValues>();
  const { groups: usersGroups, signedIn } = useAppSelector(
    (state) => state.auth,
  );
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const isAdminOrEngineer =
    usersGroups.includes(ADMIN_GROUP) || usersGroups.includes(ENGINEER_GROUP);
  const [fileSelectionStrategy, setFileSelectionStrategy] =
    useState<FileSelectionStrategy>(FileSelectionStrategy.BEST_GUESS);
  const { zippedFiles, bomFileColumns, pnpFileColumns } = useReadGerberZip(
    file,
    fileSelectionStrategy,
    selectedBoard,
    mappingSuggestionsDisabled || false,
  );
  const [showBoardSelection, setShowBoardSelection] = useState(false);

  useEffect(() => {
    setFileSelectionStrategy(FileSelectionStrategy.BEST_GUESS);
    setShowBoardSelection(false);
  }, [file]);

  useEffect(() => {
    if (selectedBoard !== null) {
      setFileSelectionStrategy(FileSelectionStrategy.PREVIOUS_UPLOAD);
    }
  }, [selectedBoard]);

  if (zippedFiles === null || values.gerberFileLayerMapping === null) {
    return null;
  }

  const duplicateLayerFiles = findDuplicateLayerFiles(
    values.gerberFileLayerMapping,
  );

  const gerberFileOptions = zippedFiles.gerberFiles.map(({ path }) => ({
    value: path,
    label: path,
  }));

  const drillFileOptions = zippedFiles.drillFiles.map(({ path }) => ({
    value: path,
    label: path,
  }));

  const bomOrPNPFileOptions = zippedFiles.bomOrPNPFiles.map(({ path }) => ({
    value: path,
    label: path,
  }));

  const additionalFileOptions = zippedFiles.additionalFiles.map(({ path }) => ({
    value: path,
    label: path,
  }));

  const bomFileColumnOptions = bomFileColumns.map((column) => ({
    value: column,
    label: column,
  }));

  const pnpFileColumnOptions = pnpFileColumns.map((column) => ({
    value: column,
    label: column,
  }));

  return (
    <GerberFileForm>
      {signedIn && !gerbersOnly && (
        <FieldGroup>
          <Checkbox
            checked={showBoardSelection}
            onChange={() => {
              // read: next state
              const willShowBoardSelection = !showBoardSelection;
              if (!willShowBoardSelection) {
                setFileSelectionStrategy(FileSelectionStrategy.BEST_GUESS);
              }
              setShowBoardSelection(willShowBoardSelection);
            }}
            label={
              "Uploading a new revision or correcting a previous file selection?"
            }
          />
          {showBoardSelection && (
            <SelectBoard
              label={"Load file selections from"}
              placeholder={"Select a previously uploaded board..."}
              selectedBoard={selectedBoard}
              setSelectedBoard={setSelectedBoard}
            />
          )}
          {isAdminOrEngineer && zippedFiles.fileMappingFile !== undefined && (
            <Checkbox
              checked={
                fileSelectionStrategy ==
                FileSelectionStrategy.INCLUDED_FILE_MAPPING
              }
              onChange={() => {
                if (
                  fileSelectionStrategy ==
                  FileSelectionStrategy.INCLUDED_FILE_MAPPING
                ) {
                  setFileSelectionStrategy(FileSelectionStrategy.BEST_GUESS);
                } else {
                  setFileSelectionStrategy(
                    FileSelectionStrategy.INCLUDED_FILE_MAPPING,
                  );
                }
                setSelectedBoard(null);
                setShowBoardSelection(false);
              }}
              label={"Use FlashPCB layer config included in zip file"}
            />
          )}
        </FieldGroup>
      )}
      <FieldGroup>
        <GerberFileFormTitle>Identify the layers</GerberFileFormTitle>
        {EXPECTED_GERBER_FILES.map((expectedGerberFile) => {
          return (
            <FormikSelect
              key={expectedGerberFile.name}
              name={`gerberFileLayerMapping.${expectedGerberFile.name}`}
              label={fieldTitle(expectedGerberFile)}
              options={
                expectedGerberFile.name == "drills"
                  ? drillFileOptions
                  : gerberFileOptions
              }
              isMulti={expectedGerberFile.multiple}
              isClearable
            />
          );
        })}
        {/* A warning only: the same file can legitimately serve two layers,
            so this does not block submission. */}
        {describeDuplicateLayerFiles(duplicateLayerFiles).map((message) => {
          return (
            <FormikErrorMessage
              key={message}
              name={"gerberFileLayerMapping"}
              errorMessage={message}
            />
          );
        })}
      </FieldGroup>
      {!gerbersOnly && (
        <>
          <FieldGroup>
            <GerberFileFormTitle>Bill of materials</GerberFileFormTitle>
            <FieldGroupNote>
              You can upload this later if you want.
            </FieldGroupNote>
            <FormikSelect
              name={"bomFileInfo.file_name"}
              label={"Bill of materials (Optional)"}
              options={bomOrPNPFileOptions}
              isClearable
            />
            {values.bomFileInfo !== null && (
              <>
                <FormikSelect
                  name={"bomFileInfo.designator_column"}
                  label={"Reference designator column*"}
                  options={bomFileColumnOptions}
                />
                <FormikSelect
                  name={"bomFileInfo.mpn_column"}
                  label={"Manufacturer's part number column*"}
                  options={bomFileColumnOptions}
                />
                <FormikSelect
                  name={"bomFileInfo.manufacturer_column"}
                  label={"Manufacturer column"}
                  options={bomFileColumnOptions}
                />
                <FormikSelect
                  name={"bomFileInfo.footprint_column"}
                  label={"Footprint column"}
                  options={bomFileColumnOptions}
                />
                <FormikSelect
                  name={"bomFileInfo.value_text_column"}
                  label={"Value text column"}
                  options={bomFileColumnOptions}
                />
                <FieldGroupNote>
                  *Required if uploading a bill of materials
                </FieldGroupNote>
              </>
            )}
          </FieldGroup>
          <FieldGroup>
            <GerberFileFormTitle>Pick and place</GerberFileFormTitle>
            <FieldGroupNote>
              This file is not required, but uploading it can reduce your board
              price. You can also upload this later if you want.
            </FieldGroupNote>
            <FormikSelect
              name={"pnpFileInfo.file_name"}
              label={
                <>
                  Pick and place (Optional{" "}
                  <Link
                    href={"/docs/uploading-your-board#required-files"}
                    target={"_blank"}
                  >
                    see docs
                  </Link>
                  )
                </>
              }
              options={bomOrPNPFileOptions}
              isClearable
            />
            {values.pnpFileInfo !== null && (
              <>
                <FormikSelect
                  name={"pnpFileInfo.designator_column"}
                  label={"Reference designator column**"}
                  options={pnpFileColumnOptions}
                />
                <FormikSelect
                  name={"pnpFileInfo.x_column"}
                  label={"X position column**"}
                  options={pnpFileColumnOptions}
                />
                <FormikSelect
                  name={"pnpFileInfo.y_column"}
                  label={"Y position column**"}
                  options={pnpFileColumnOptions}
                />
                <FormikSelect
                  name={"pnpFileInfo.unit"}
                  label={"X, Y unit**"}
                  options={UNIT_OPTIONS}
                />
                <FormikSelect
                  name={"pnpFileInfo.rotation_column"}
                  label={"Rotation column (degrees)**"}
                  options={pnpFileColumnOptions}
                />
                <FormikSelect
                  name={"pnpFileInfo.mount_type_column"}
                  label={"Mount side column (Top / Bottom)**"}
                  options={pnpFileColumnOptions}
                />
                <FieldGroupNote>
                  **Required if uploading a pick and place file
                </FieldGroupNote>
              </>
            )}
          </FieldGroup>
          <FieldGroup>
            <GerberFileFormTitle>Assembly drawing(s)</GerberFileFormTitle>
            <FieldGroupNote>
              This file is not required, but uploading it can eliminate the need
              to share it later.
            </FieldGroupNote>
            <FormikSelect
              name={"assemblyDrawing"}
              label={
                <>
                  Assembly drawing (Optional{" "}
                  <Link
                    href={"/docs/uploading-your-board#required-files"}
                    target={"_blank"}
                  >
                    see docs
                  </Link>
                  )
                </>
              }
              options={additionalFileOptions}
              isClearable
              isMulti={true}
            />
          </FieldGroup>
          <FieldGroup>
            <GerberFileFormTitle>Fab drawing(s)</GerberFileFormTitle>
            <FieldGroupNote>
              This file is not required, but uploading it can eliminate the need
              to share it later.
            </FieldGroupNote>
            <FormikSelect
              name={"fabDrawing"}
              label={
                <>
                  Fab drawing (Optional{" "}
                  <Link
                    href={"/docs/uploading-your-board#required-files"}
                    target={"_blank"}
                  >
                    see docs
                  </Link>
                  )
                </>
              }
              options={additionalFileOptions}
              isClearable
              isMulti={true}
            />
          </FieldGroup>
          <FieldGroup>
            <GerberFileFormTitle>
              Additional reference image(s) / file(s)
            </GerberFileFormTitle>
            <FieldGroupNote>
              This file is not required, but uploading it can eliminate the need
              to share it later.
            </FieldGroupNote>
            <FormikSelect
              name={"referenceFile"}
              label={
                <>
                  Reference file (Optional{" "}
                  <Link
                    href={"/docs/uploading-your-board#required-files"}
                    target={"_blank"}
                  >
                    see docs
                  </Link>
                  )
                </>
              }
              options={additionalFileOptions}
              isClearable
              isMulti={true}
            />
          </FieldGroup>
        </>
      )}
    </GerberFileForm>
  );
}
